import React, { useState, useCallback } from 'react';
import { getCountries, getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import rpnFlags from 'react-phone-number-input/flags';
import V2Button from '../../../components/button/v2_Button';
import V2Icon from '../../../components/icon/v2_Icon';
import { useV2Theme } from '../../../themecontrols/v2_ThemeContext';
import cloobotXLogoDark from '../../../assets/Cloobot X Logo.svg';
import cloobotXLogoLight from '../../../assets/Cloobot X Logo__Light.png';
import './V2SignupCard.css';

/* ── Module-level constants — computed once at load time ── */
const SORTED_COUNTRIES = getCountries().sort((a, b) => (en[a] || a).localeCompare(en[b] || b));

function V2SignupCard({
  fullName, email, orgName, password, showPassword, loading, error, success, phoneError,
  setFullName, setEmail, setOrgName, setPassword, setPhone, setDialCode,
  setSelectedCountry, setShowPassword, setIsPhoneValid,
  onBackToLogin, onOktaClick, onSignup,
}) {
  const { theme } = useV2Theme();
  const cloobotXLogo = theme === 'light' ? cloobotXLogoLight : cloobotXLogoDark;

  const [countryCode, setCountryCode] = useState('US');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryFocused, setCountryFocused] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState('');

  const FlagComponent = rpnFlags[countryCode];

  const handleCountryChange = useCallback((e) => {
    const cc = e.target.value;
    setCountryCode(cc);
    const dc = String(getCountryCallingCode(cc));
    setDialCode(dc);
    setSelectedCountry({ name: en[cc] || cc, code: cc, dial_code: dc });
    if (phoneNumber) {
      try {
        setIsPhoneValid(isValidPhoneNumber(`+${dc}${phoneNumber}`));
      } catch (_) {
        setIsPhoneValid(false);
      }
    }
  }, [phoneNumber, setDialCode, setSelectedCountry, setIsPhoneValid]);

  const handlePhoneNumberChange = useCallback((e) => {
    const val = e.target.value.replace(/\D/g, '');
    setPhoneNumber(val);
    setPhone(val);
    if (val) {
      try {
        const dc = String(getCountryCallingCode(countryCode));
        setIsPhoneValid(isValidPhoneNumber(`+${dc}${val}`));
      } catch (_) {
        setIsPhoneValid(false);
      }
    } else {
      setIsPhoneValid(true);
    }
  }, [countryCode, setPhone, setIsPhoneValid]);

  const handleSignup = useCallback((e) => {
    e.preventDefault();
    setPasswordMatchError('');
    
    if (password !== confirmPassword) {
      setPasswordMatchError('Password fields are not matching');
      return;
    }
    
    onSignup();
  }, [password, confirmPassword, onSignup]);

  return (
    <div className="v2-signup-card">
      <img src={cloobotXLogo} alt="CloobotX" className="v2-signup-card__logo" />
      {/* <h1 className="v2-signup-card__title">Create your account</h1> */}
      {/* <p className="v2-signup-card__subtitle">Get started with CloobotX</p> */}

      {error && <div className="v2-signup-card__alert v2-signup-card__alert--error">{error}</div>}
      {success && <div className="v2-signup-card__alert v2-signup-card__alert--success">{success}</div>}
      {passwordMatchError && <div className="v2-signup-card__alert v2-signup-card__alert--error">{passwordMatchError}</div>}

      <form className="v2-signup-card__form" onSubmit={handleSignup}>
        <div className="v2-signup-card__field">
          <label className="v2-signup-card__label" htmlFor="v2-signup-name">
            Full name <span className="v2-signup-card__required">*</span>
          </label>
          <div className="v2-signup-card__input-wrap">
            <V2Icon name="person" className="v2-signup-card__input-icon" />
            <input
              id="v2-signup-name"
              type="text"
              autoComplete="name"
              autoFocus
              className="v2-signup-card__input"
              value={fullName}
              placeholder="Jane Doe"
              onChange={(e) => setFullName(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="v2-signup-card__field">
          <label className="v2-signup-card__label" htmlFor="v2-signup-email">
            Email <span className="v2-signup-card__required">*</span>
          </label>
          <div className="v2-signup-card__input-wrap">
            <V2Icon name="mail" className="v2-signup-card__input-icon" />
            <input
              id="v2-signup-email"
              type="email"
              autoComplete="email"
              className="v2-signup-card__input"
              value={email}
              placeholder="you@company.com"
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="v2-signup-card__field">
          <label className="v2-signup-card__label" htmlFor="v2-signup-org">
            Organisation name <span className="v2-signup-card__required">*</span>
          </label>
          <div className="v2-signup-card__input-wrap">
            <V2Icon name="corporate_fare" className="v2-signup-card__input-icon" />
            <input
              id="v2-signup-org"
              type="text"
              className="v2-signup-card__input"
              value={orgName}
              placeholder="Acme Corp"
              onChange={(e) => setOrgName(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="v2-signup-card__field">
          <label className="v2-signup-card__label" htmlFor="v2-signup-phone">
            Phone No.
          </label>
          <div className="v2-signup-card__phone-row">
            <div className={`v2-signup-card__country-wrap${countryFocused ? ' is-focused' : ''}`}>
              {FlagComponent && (
                <FlagComponent
                  title={en[countryCode] || countryCode}
                  className="v2-signup-card__flag-svg"
                />
              )}
              <span className="v2-signup-card__dial-code">+{getCountryCallingCode(countryCode)}</span>
              <V2Icon name="keyboard_arrow_down" className="v2-signup-card__chevron" />
              <select
                className="v2-signup-card__country-select"
                value={countryCode}
                onChange={handleCountryChange}
                onFocus={() => setCountryFocused(true)}
                onBlur={() => setCountryFocused(false)}
                disabled={loading}
                aria-label="Country calling code"
              >
                {SORTED_COUNTRIES.map((cc) => (
                  <option key={cc} value={cc}>
                    {en[cc] || cc} (+{getCountryCallingCode(cc)})
                  </option>
                ))}
              </select>
            </div>
            <input
              type="tel"
              id="v2-signup-phone"
              className="v2-signup-card__phone-num-input"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              placeholder="Phone No"
              disabled={loading}
            />
          </div>
          {phoneError && <p className="v2-signup-card__field-error">{phoneError}</p>}
        </div>

        <div className="v2-signup-card__field">
          <label className="v2-signup-card__label" htmlFor="v2-signup-password">
            Password <span className="v2-signup-card__required">*</span>
          </label>
          <div className="v2-signup-card__input-wrap">
            <V2Icon name="lock" className="v2-signup-card__input-icon" />
            <input
              id="v2-signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="v2-signup-card__input"
              value={password}
              placeholder="Create a password"
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <button
              type="button"
              className="v2-signup-card__pw-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <V2Icon name={showPassword ? 'visibility_off' : 'visibility'} />
            </button>
          </div>
        </div>

        <div className="v2-signup-card__field">
          <label className="v2-signup-card__label" htmlFor="v2-signup-confirm-password">
            Confirm password <span className="v2-signup-card__required">*</span>
          </label>
          <div className="v2-signup-card__input-wrap">
            <V2Icon name="lock" className="v2-signup-card__input-icon" />
            <input
              id="v2-signup-confirm-password"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              className="v2-signup-card__input"
              value={confirmPassword}
              placeholder="Confirm your password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
            <button
              type="button"
              className="v2-signup-card__pw-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              <V2Icon name={showConfirmPassword ? 'visibility_off' : 'visibility'} />
            </button>
          </div>
        </div>

        <V2Button variant="primary" type="submit" disabled={loading} className="v2-signup-card__submit">
          {loading ? 'Creating account...' : 'Create account'}
        </V2Button>
      </form>

      <div className="v2-signup-card__divider"><span>or</span></div>

      <V2Button
        variant="secondary"
        onClick={() => onOktaClick('signup')}
        disabled={loading}
        className="v2-signup-card__okta-btn"
      >
        <V2Icon name="business" />
        Sign up with Okta SSO
      </V2Button>

      <div className="v2-signup-card__footer">
        <span>Already have an account?</span>
        <V2Button variant="subtle" onClick={onBackToLogin} disabled={loading}>
          Sign in
        </V2Button>
      </div>

      <div className="v2-signup-card__terms">
        By signing up you agree to the{' '}
        <a
          href="https://cloobot-public.s3.ap-south-1.amazonaws.com/Cloobot+Product+Suite+Terms+and+Conditions.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="v2-signup-card__terms-link"
        >
          Terms &amp; Conditions
        </a>
      </div>
    </div>
  );
}

export default V2SignupCard;
